import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { Section, TextField } from "@/pages/admin/fields/primitives";
import { StringListEditor } from "@/pages/admin/fields/StringListEditor";

const TEXT = [
  ["prepared_for", "Label (top right)"],
  ["s1_heading", "01 — Heading"], ["s1_body", "01 — Supporting text", true],
  ["s2_heading", "02 — Heading"], ["s2_body", "02 — Supporting text", true],
  ["s3_heading", "03 — Heading"], ["s3_body", "03 — Supporting text", true],
  ["manual_heading", "Brand-manual block heading"],
  ["s4_heading", "04 — Heading"], ["s4_note", "04 — Business-plan note", true],
  ["s5_heading", "05 — Heading"], ["s5_intro", "05 — Intro", true],
  ["closing_heading", "Closing heading"], ["closing_body", "Closing text", true],
];

const ProjectSelect = ({ label, value, onChange, projects, testId, optional }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">{label}</label>
    <select
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
    >
      {optional && <option value="">— None —</option>}
      {projects.map((p) => (
        <option key={p.slug} value={p.slug}>{p.title} {p.status === "draft" ? "(draft)" : ""} — {p.slug}</option>
      ))}
    </select>
  </div>
);

export const PresentationPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [keySet, setKeySet] = useState(false);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/presentations/qredits").then((r) => { setCfg(r.data.config); setKeySet(r.data.access_key_configured); });
    api.get("/admin/portfolio").then((r) => setProjects(r.data || []));
  }, []);

  if (!cfg) return <div className="text-sm text-[#A3A3A3]">Loading…</div>;
  const set = (k) => (v) => setCfg({ ...cfg, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/presentations/qredits", cfg);
      setCfg(r.data.config);
      toast.success("Presentation saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="presentation-panel" className="space-y-8 max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display font-black text-2xl">Private Presentation — Qredits Aruba</h2>
          <p className="text-sm text-[#A3A3A3] mt-1">
            Unlisted page at <code>/p/qredits-aruba</code> · access key {keySet ? "configured" : <b className="text-[#FF6600]">NOT configured (page locked)</b>}
          </p>
        </div>
        <div className="flex gap-2">
          <a href="/p/qredits-aruba" target="_blank" rel="noreferrer" data-testid="presentation-open" className="ur-btn-secondary !py-2.5 text-sm">
            Open <ExternalLink className="w-4 h-4" />
          </a>
          <button data-testid="presentation-save" onClick={save} disabled={saving} className="ur-btn-secondary ur-btn-secondary--fill !py-2.5 text-sm">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <Section title="Projects & imagery" hint="Images, names, descriptions and image ORDER come from the selected Portfolio projects. Edit them in Portfolio — use DRAFT projects so the public portfolio never changes.">
        <div className="grid gap-5">
          <ProjectSelect label="01 — Hero image (uses cover)" value={cfg.hero_project} onChange={set("hero_project")} projects={projects} testId="pres-select-hero" />
          <ProjectSelect label="03 — Centerpiece brand (cover + gallery, in gallery order)" value={cfg.centerpiece_project} onChange={set("centerpiece_project")} projects={projects} testId="pres-select-centerpiece" />
          <ProjectSelect label="03 — Optional brand-manual spreads (gallery images)" value={cfg.manual_project} onChange={set("manual_project")} projects={projects} testId="pres-select-manual" optional />
          <div>
            <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">02 — Proof projects (slugs, in display order)</label>
            <div className="mt-2"><StringListEditor value={cfg.proof_projects} onChange={set("proof_projects")} testId="pres-proof-list" /></div>
            <div className="text-[11px] text-[#A3A3A3] mt-1">Available: {projects.map((p) => p.slug).join(", ")}</div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">02 — "Also trusted by" clients</label>
            <div className="mt-2"><StringListEditor value={cfg.established_clients} onChange={set("established_clients")} testId="pres-clients-list" /></div>
          </div>
        </div>
      </Section>

      <Section title="Text">
        <div className="grid gap-5">
          {TEXT.map(([k, label, area]) => (
            <TextField key={k} label={label} value={cfg[k]} textarea={!!area} onChange={(e) => set(k)(e.target.value)} />
          ))}
          <div>
            <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">04 — Steps</label>
            <div className="mt-2"><StringListEditor value={cfg.s4_steps} onChange={set("s4_steps")} testId="pres-steps-list" /></div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">05 — Pilot points</label>
            <div className="mt-2"><StringListEditor value={cfg.s5_points} onChange={set("s5_points")} testId="pres-points-list" /></div>
          </div>
        </div>
      </Section>
    </div>
  );
};
